import { useState } from "react";

// ── helpers (module-level so both modes can use them) ─────────────────────────

function getStartOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function dateKeyFromDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatShortDate(dateKey) {
  const [, m, d] = dateKey.split("-");
  return `${parseInt(m)}/${parseInt(d)}`;
}

function formatFullDate(dateKey) {
  const [y, m, d] = dateKey.split("-");
  return `${parseInt(m)}/${parseInt(d)}/${y.slice(2)}`;
}

/** Returns the Mon dateKey for the week containing dateKey */
function weekStartForDateKey(dateKey) {
  const d = new Date(`${dateKey}T12:00:00`);
  return dateKeyFromDate(getStartOfWeek(d));
}

/** Build 7 dateKeys Mon–Sun starting from mondayDateKey */
function buildWeekDays(mondayDateKey) {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(`${mondayDateKey}T12:00:00`);
    d.setDate(d.getDate() + i);
    return dateKeyFromDate(d);
  });
}

const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

/** Compute stats for one week */
function computeWeekStats({
  weekDays,
  dailySelections,
  sideQuestCompletions,
  revivedDays,
  challengeCosts,
  customHabits,
  gameContent,
  weekStartDate, // Date object for the Monday
}) {
  const defaultHabits = {
    nutrition: { low: { xp: 10 }, main: { xp: 20 }, high: { xp: 30 } },
    movement:  { low: { xp: 10 }, main: { xp: 20 }, high: { xp: 30 } },
    mindset:   { low: { xp: 10 }, main: { xp: 20 }, high: { xp: 30 } },
  };
  const habits = customHabits || defaultHabits;

  const weeklyChallenges = (gameContent?.weeklyChallenges || []).filter((c) => c.active);
  const activeSideQuests = (gameContent?.sideQuests || []).filter((q) => q.active);
  const sideQuestXPMap = Object.fromEntries(activeSideQuests.map((q) => [q.title, q.xp]));

  // Determine weekly challenge XP for this week
  let weeklyXP = 0;
  if (weeklyChallenges.length > 0) {
    const seed = Math.floor(weekStartDate.getTime() / (1000 * 60 * 60 * 24 * 7));
    weeklyXP = weeklyChallenges[Math.abs(seed) % weeklyChallenges.length]?.xp || 0;
  }

  let daysCompleted = 0;
  let nutritionDays = 0;
  let movementDays = 0;
  let mindsetDays = 0;
  let weeklyChallengeDays = 0; // how many days the weekly was marked (usually 0 or 1)
  let totalXPEarned = 0;
  let sideQuestCount = 0;

  // Revives / tokens used on days within this week
  let revivesUsed = 0;
  let tokensUsed = 0;

  weekDays.forEach((dateKey) => {
    const sel = dailySelections[dateKey] || {};
    const revive = revivedDays?.[dateKey] || {};

    if (Boolean(sel.nutrition)) {
      nutritionDays++;
      totalXPEarned += habits.nutrition?.[sel.nutrition]?.xp || 0;
    }
    if (Boolean(sel.movement)) {
      movementDays++;
      totalXPEarned += habits.movement?.[sel.movement]?.xp || 0;
    }
    if (Boolean(sel.mindset)) {
      mindsetDays++;
      totalXPEarned += habits.mindset?.[sel.mindset]?.xp || 0;
    }
    if (Boolean(sel.nutrition) && Boolean(sel.movement) && Boolean(sel.mindset)) {
      daysCompleted++;
    }
    if (sel.weekly) {
      weeklyChallengeDays++;
      totalXPEarned += weeklyXP;
    }

    // Count revives/tokens applied to this date
    if (revive.nutrition || revive.movement || revive.mindset) {
      const reviveCount = [revive.nutrition, revive.movement, revive.mindset].filter(Boolean).length;
      // If all 3 were revived it was a full revive, otherwise individual tokens
      const originalMissed = [!sel.nutrition, !sel.movement, !sel.mindset].filter(Boolean).length;
      if (originalMissed === 3 && reviveCount === 3) {
        revivesUsed++;
      } else {
        tokensUsed += reviveCount;
      }
    }

    // Side quests
    const sq = sideQuestCompletions?.[dateKey] || {};
    Object.entries(sq).forEach(([title, done]) => {
      if (done) {
        sideQuestCount++;
        totalXPEarned += sideQuestXPMap[title] || 0;
      }
    });
  });

  return {
    daysCompleted,
    nutritionDays,
    movementDays,
    mindsetDays,
    weeklyChallengeDays,
    totalXPEarned,
    sideQuestCount,
    revivesUsed,
    tokensUsed,
  };
}

function headlineFor(daysCompleted) {
  if (daysCompleted === 7) return "🏆 Perfect week!";
  if (daysCompleted >= 5) return "🔥 Strong week!";
  if (daysCompleted >= 3) return "💪 Solid effort!";
  if (daysCompleted >= 1) return "🌱 Keep building!";
  return "📅 No full days";
}

// ── Single-week card ──────────────────────────────────────────────────────────

function SingleWeekCard({
  weekDays,
  weekStartDate,
  dailySelections,
  sideQuestCompletions,
  revivedDays,
  challengeCosts,
  customHabits,
  gameContent,
  name,
  categoryLabels = {},
}) {
  const stats = computeWeekStats({
    weekDays,
    dailySelections,
    sideQuestCompletions,
    revivedDays,
    challengeCosts,
    customHabits,
    gameContent,
    weekStartDate,
  });

  const weekStartLabel = formatShortDate(weekDays[0]);
  const weekEndLabel   = formatShortDate(weekDays[6]);

  const statItems = [
    { emoji: "🔥", label: "Full Quest Days",   value: `${stats.daysCompleted}/7` },
    { emoji: "🍓", label: categoryLabels.nutrition || "Nutrition", value: `${stats.nutritionDays}/7` },
    { emoji: "🚶‍♀️", label: categoryLabels.movement || "Movement", value: `${stats.movementDays}/7` },
    { emoji: "🌙", label: categoryLabels.mindset  || "Mindset",   value: `${stats.mindsetDays}/7` },
    { emoji: "⭐", label: "Weekly Challenge",   value: stats.weeklyChallengeDays > 0 ? "Done ✓" : "Missed" },
    { emoji: "✨", label: "Side Quests",        value: stats.sideQuestCount },
    { emoji: "💎", label: "XP Earned",          value: `+${stats.totalXPEarned}` },
  ];

  return (
    <div style={{
      background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
      borderRadius: "22px", padding: "20px", marginTop: "20px",
      border: "2px solid #7dd3fc",
      boxShadow: "0 8px 20px rgba(30, 58, 138, 0.10)",
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "14px" }}>
        <div>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "18px" }}>
            {headlineFor(stats.daysCompleted)}
          </div>
          <div style={{ color: "#4b6b8a", fontSize: "13px", marginTop: "2px" }}>
            {name ? `${name}'s week of ` : "Week of "}
            {weekStartLabel} – {weekEndLabel}
          </div>
        </div>
        <div style={{
          background: "#dbeafe", borderRadius: "12px", padding: "6px 12px",
          color: "#1e3a5f", fontWeight: "bold", fontSize: "13px",
          border: "2px solid #93c5fd",
        }}>Last Week</div>
      </div>

      {/* Day dots */}
      <div style={{ display: "flex", gap: "6px", marginBottom: "16px" }}>
        {weekDays.map((dateKey, idx) => {
          const sel = dailySelections[dateKey] || {};
          const rev = revivedDays?.[dateKey] || {};
          const full = (Boolean(sel.nutrition) || Boolean(rev.nutrition)) &&
                       (Boolean(sel.movement)  || Boolean(rev.movement))  &&
                       (Boolean(sel.mindset)   || Boolean(rev.mindset));
          const partial = !full && (Boolean(sel.nutrition) || Boolean(sel.movement) || Boolean(sel.mindset));
          return (
            <div key={dateKey} style={{ flex: 1, textAlign: "center" }}>
              <div style={{
                width: "100%", aspectRatio: "1/1", borderRadius: "10px",
                background: full ? "#2563eb" : partial ? "#93c5fd" : "#dbeafe",
                border: full ? "2px solid #1d4ed8" : "2px solid #bfdbfe",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "14px",
              }}>
                {full ? "✓" : partial ? "·" : ""}
              </div>
              <div style={{ color: "#6b8cb0", fontSize: "10px", marginTop: "3px" }}>{DAY_LABELS[idx]}</div>
            </div>
          );
        })}
      </div>

      {/* Stats grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
        {statItems.map(({ emoji, label, value }) => (
          <div key={label} style={{
            background: "rgba(255,255,255,0.7)", borderRadius: "14px",
            padding: "10px 12px", border: "1px solid #bfdbfe",
            display: "flex", alignItems: "center", gap: "8px",
          }}>
            <span style={{ fontSize: "18px", flexShrink: 0 }}>{emoji}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#6b8cb0", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.3px" }}>{label}</div>
              <div style={{ color: "#1e3a5f", fontWeight: "bold", fontSize: "15px" }}>{value}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── History log row ───────────────────────────────────────────────────────────

function WeekHistoryRow({ weekDays, weekStartDate, dailySelections, sideQuestCompletions, revivedDays, challengeCosts, customHabits, gameContent }) {
  const stats = computeWeekStats({
    weekDays, dailySelections, sideQuestCompletions, revivedDays, challengeCosts,
    customHabits, gameContent, weekStartDate,
  });

  const weekStartLabel = formatFullDate(weekDays[0]);
  const weekEndLabel   = formatFullDate(weekDays[6]);

  const fullColor = stats.daysCompleted === 7 ? "#16a34a"
    : stats.daysCompleted >= 5 ? "#2563eb"
    : stats.daysCompleted >= 3 ? "#d97706"
    : "#dc2626";

  return (
    <div style={{
      background: "#f8fbff", borderRadius: "18px", padding: "16px",
      border: "2px solid #bfdbfe",
    }}>
      {/* Week header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "6px" }}>
        <div>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px" }}>
            {weekStartLabel} – {weekEndLabel}
          </div>
          <div style={{ color: fullColor, fontSize: "13px", fontWeight: "bold", marginTop: "2px" }}>
            {headlineFor(stats.daysCompleted)}
          </div>
        </div>
        <div style={{
          background: "#e0f2fe", borderRadius: "10px", padding: "4px 10px",
          color: "#1e3a5f", fontWeight: "bold", fontSize: "13px",
          border: "1px solid #93c5fd",
        }}>
          💎 +{stats.totalXPEarned} XP
        </div>
      </div>

      {/* Day dots */}
      <div style={{ display: "flex", gap: "4px", marginBottom: "12px" }}>
        {weekDays.map((dateKey, idx) => {
          const sel = dailySelections[dateKey] || {};
          const rev = revivedDays?.[dateKey] || {};
          const full = (Boolean(sel.nutrition) || Boolean(rev.nutrition)) &&
                       (Boolean(sel.movement)  || Boolean(rev.movement))  &&
                       (Boolean(sel.mindset)   || Boolean(rev.mindset));
          const partial = !full && (Boolean(sel.nutrition) || Boolean(sel.movement) || Boolean(sel.mindset));
          const wasRevived = Boolean(rev.nutrition || rev.movement || rev.mindset);
          return (
            <div key={dateKey} style={{ flex: 1, textAlign: "center" }}>
              <div style={{
                width: "100%", aspectRatio: "1/1", borderRadius: "8px",
                background: full ? (wasRevived ? "#a78bfa" : "#2563eb") : partial ? "#93c5fd" : "#dbeafe",
                border: full ? `2px solid ${wasRevived ? "#7c3aed" : "#1d4ed8"}` : "2px solid #bfdbfe",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "11px", color: "#fff",
              }}>
                {full ? (wasRevived ? "↩" : "✓") : partial ? "·" : ""}
              </div>
              <div style={{ color: "#6b8cb0", fontSize: "9px", marginTop: "2px" }}>{DAY_LABELS[idx]}</div>
            </div>
          );
        })}
      </div>

      {/* Stats chips */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {[
          { emoji: "🔥", label: `${stats.daysCompleted}/7 full days` },
          { emoji: "🍓", label: `${stats.nutritionDays}/7 nutrition` },
          { emoji: "🚶‍♀️", label: `${stats.movementDays}/7 movement` },
          { emoji: "🌙", label: `${stats.mindsetDays}/7 mindset` },
          { emoji: "⭐", label: stats.weeklyChallengeDays > 0 ? "Weekly ✓" : "Weekly ✗" },
          ...(stats.sideQuestCount > 0 ? [{ emoji: "✨", label: `${stats.sideQuestCount} side quest${stats.sideQuestCount !== 1 ? "s" : ""}` }] : []),
          ...(stats.revivesUsed > 0 ? [{ emoji: "💖", label: `${stats.revivesUsed} revive${stats.revivesUsed !== 1 ? "s" : ""}` }] : []),
          ...(stats.tokensUsed > 0 ? [{ emoji: "🪙", label: `${stats.tokensUsed} token${stats.tokensUsed !== 1 ? "s" : ""}` }] : []),
        ].map(({ emoji, label }) => (
          <span key={label} style={{
            padding: "3px 9px", borderRadius: "999px",
            background: "#f0f9ff", border: "1px solid #bfdbfe",
            fontSize: "12px", color: "#1e3a5f", fontWeight: "bold",
          }}>
            {emoji} {label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function WeeklyRecap({
  mode = "single",           // "single" | "history" | "navigable"
  dailySelections = {},
  sideQuestCompletions = {},
  revivedDays = {},
  challengeCosts = {},
  customHabits,
  gameContent = {},
  profileName = "",
  isAdmin = false,
  adminUserName = "",
  categoryLabels = {},
}) {
  const [navIdx, setNavIdx] = useState(0);

  // ── Determine which weeks to show ─────────────────────────────────────────
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const thisWeekStart = getStartOfWeek(today);

  // Collect all unique week-start keys from the data
  const allDateKeys = [
    ...Object.keys(dailySelections),
    ...Object.keys(sideQuestCompletions),
    ...Object.keys(revivedDays),
  ];

  const weekStartKeys = new Set(allDateKeys.map(weekStartForDateKey));

  // For "single" mode: only last completed week
  // For "history"/"navigable" mode: all weeks with data, excluding current week, newest first
  const lastWeekStart = new Date(thisWeekStart);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);
  const lastWeekStartKey = dateKeyFromDate(lastWeekStart);

  let weeksToShow = [];

  if (mode === "history" || mode === "navigable") {
    weeksToShow = [...weekStartKeys]
      .filter((k) => k < dateKeyFromDate(thisWeekStart)) // exclude current week
      .sort((a, b) => (a < b ? 1 : -1)); // newest first
  } else {
    // single: just last week
    if (weekStartKeys.has(lastWeekStartKey)) {
      weeksToShow = [lastWeekStartKey];
    } else {
      // check if there's any data in last week even if not in the set
      const lastWeekDays = buildWeekDays(lastWeekStartKey);
      const hasData = lastWeekDays.some((dk) =>
        dailySelections[dk] || sideQuestCompletions[dk] || revivedDays?.[dk]
      );
      if (hasData) weeksToShow = [lastWeekStartKey];
    }
  }

  const name = isAdmin ? adminUserName : profileName;

  // ── Navigable mode (one week at a time with arrows) ───────────────────────
  if (mode === "navigable") {
    if (weeksToShow.length === 0) {
      return (
        <div style={{ background: "#f8fbff", borderRadius: "18px", padding: "18px", border: "2px solid #bfdbfe", color: "#4b6b8a", fontSize: "14px" }}>
          📅 Weekly Recap — no completed weeks yet.
        </div>
      );
    }
    const clampedIdx = Math.min(navIdx, weeksToShow.length - 1);
    const mondayKey = weeksToShow[clampedIdx];
    const weekDays = buildWeekDays(mondayKey);
    const weekStartDate = new Date(`${mondayKey}T12:00:00`);
    const canGoOlder = clampedIdx < weeksToShow.length - 1;
    const canGoNewer = clampedIdx > 0;
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px" }}>📅 Weekly Recap</div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <button
              onClick={() => setNavIdx(clampedIdx + 1)}
              disabled={!canGoOlder}
              style={{ padding: "4px 12px", borderRadius: "8px", border: "2px solid #bfdbfe", background: canGoOlder ? "#f5faff" : "#f0f0f0", color: canGoOlder ? "#1e3a5f" : "#aaa", fontWeight: "bold", cursor: canGoOlder ? "pointer" : "default", fontSize: "13px" }}
            >← Older</button>
            <span style={{ color: "#4b6b8a", fontSize: "12px", minWidth: "120px", textAlign: "center" }}>
              {formatShortDate(weekDays[0])} – {formatShortDate(weekDays[6])}
            </span>
            <button
              onClick={() => setNavIdx(clampedIdx - 1)}
              disabled={!canGoNewer}
              style={{ padding: "4px 12px", borderRadius: "8px", border: "2px solid #bfdbfe", background: canGoNewer ? "#f5faff" : "#f0f0f0", color: canGoNewer ? "#1e3a5f" : "#aaa", fontWeight: "bold", cursor: canGoNewer ? "pointer" : "default", fontSize: "13px" }}
            >Newer →</button>
          </div>
        </div>
        <WeekHistoryRow
          weekDays={weekDays}
          weekStartDate={weekStartDate}
          dailySelections={dailySelections}
          sideQuestCompletions={sideQuestCompletions}
          revivedDays={revivedDays}
          challengeCosts={challengeCosts}
          customHabits={customHabits}
          gameContent={gameContent}
          categoryLabels={categoryLabels}
        />
      </div>
    );
  }

  if (weeksToShow.length === 0) return null;

  // ── History mode ──────────────────────────────────────────────────────────
  if (mode === "history") {
    return (
      <div style={{ display: "grid", gap: "12px" }}>
        {weeksToShow.map((mondayKey) => {
          const weekDays = buildWeekDays(mondayKey);
          const weekStartDate = new Date(`${mondayKey}T12:00:00`);
          return (
            <WeekHistoryRow
              key={mondayKey}
              weekDays={weekDays}
              weekStartDate={weekStartDate}
              dailySelections={dailySelections}
              sideQuestCompletions={sideQuestCompletions}
              revivedDays={revivedDays}
              challengeCosts={challengeCosts}
              customHabits={customHabits}
              gameContent={gameContent}
            />
          );
        })}
      </div>
    );
  }

  // ── Single-week card mode ─────────────────────────────────────────────────
  const mondayKey = weeksToShow[0];
  const weekDays = buildWeekDays(mondayKey);
  const weekStartDate = new Date(`${mondayKey}T12:00:00`);

  return (
    <SingleWeekCard
      weekDays={weekDays}
      weekStartDate={weekStartDate}
      dailySelections={dailySelections}
      sideQuestCompletions={sideQuestCompletions}
      revivedDays={revivedDays}
      challengeCosts={challengeCosts}
      customHabits={customHabits}
      gameContent={gameContent}
      name={name}
      categoryLabels={categoryLabels}
    />
  );
}
